/**
 * Parse API error to user-friendly message
 * Backend returns: { error: { code, message, details } }
 */
export const parseApiError = (error) => {
  // Check for API error format
  const apiError = error.response?.data?.error;
  if (apiError) {
    return {
      code: apiError.code || 'UNKNOWN_ERROR',
      message: apiError.message || 'An error occurred',
      details: apiError.details,
      status: error.response?.status,
      isRetryable: isRetryableError(error),
    };
  }

  // Network or other errors
  if (error.code === 'ECONNABORTED') {
    return {
      code: 'TIMEOUT',
      message: 'The request timed out. Please check your connection and try again.',
      isRetryable: true,
    };
  }

  if (error.message === 'Network Error') {
    return {
      code: 'NETWORK_ERROR',
      message: 'Unable to connect to the server. Please check your internet connection.',
      isRetryable: true,
    };
  }

  // Session expired error (custom error from auth interceptor)
  if (error.name === 'AuthSessionExpiredError') {
    return {
      code: 'SESSION_EXPIRED',
      message: error.message || 'Your session has expired. Please log in again.',
      isRetryable: false,
    };
  }

  return {
    code: 'UNKNOWN_ERROR',
    message: error.message || 'An unexpected error occurred',
    isRetryable: false,
  };
};

/**
 * Check if error is retryable
 */
export const isRetryableError = (error) => {
  const status = error.response?.status;
  if (!status) return true; // Network errors are retryable
  return status >= 500 || status === 408 || status === 429;
};

/**
 * Get user-friendly error message based on HTTP status
 */
export const getErrorMessageForStatus = (status) => {
  const messages = {
    400: 'Invalid request. Please check your input and try again.',
    401: 'Your session has expired. Please log in again.',
    403: 'You do not have permission to perform this action.',
    404: 'The requested resource was not found.',
    409: 'This operation conflicts with existing data.',
    429: 'Too many requests. Please wait a moment and try again.',
    500: 'Our servers are experiencing issues. Please try again later.',
    502: 'Service temporarily unavailable. Please try again later.',
    503: 'Service temporarily unavailable. Please try again later.',
  };
  return messages[status] || 'An unexpected error occurred.';
};

/**
 * Custom error class for session expiry
 */
export class AuthSessionExpiredError extends Error {
  constructor(message = 'Your session has expired. Please log in again.') {
    super(message);
    this.name = 'AuthSessionExpiredError';
  }
}
